import { useEffect, useRef, useState } from 'react';

/**
 * One-shot scroll reveal.
 *
 * Attach the returned ref to an element; `visible` flips to `true` the first
 * time it intersects the viewport, then the observer disconnects — the
 * element never fades back out on scroll-up.
 */
export function useFadeUp<T extends HTMLElement = HTMLDivElement>(
  threshold = 0.15,
): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, visible];
}
